<template>
    <div class="w-full h-full flex overflow-x-scroll border-gray-200 border" :style="targetListStyle">
        <table class="w-full h-full border-separate border-spacing-0 bg-gray-50">
            <thead class="-top-[1px] left-0 right-0 relative">
                <th 
                    v-for="(header, index) in columnHeaders" 
                    :key="header.fieldName" 
                    class=" border-r border-b-2 whitespace-nowrap border-gray-200 border-b-gray-300 bg-white text-gray-700 tracking-widest font-semibold text-xs sticky -top-[1px]"
                    :style="getHeaderStyleByIndex(index)"
                    >
                    <div v-if="index==0" class="flex justify-between items-center h-full pl-[7px] pr-1.5">
                        <div class="">
                            <SelectAll
                                :selectAll="selectAll"
                                @toggle-select-all="toggleSelectAll"
                            />
                        </div>
                        <div class="flex flex-col h-[18px] w-[18px] rounded justify-center items-center hover:bg-indigo-200 cursor-pointer">
                            <div v-if="expandAll=='expanded'" class="w-full h-full flex items-center justify-center" @click="setExpandAll('collapsed')"><ChevronUpMiniSVG /></div>
                            <div v-else class="w-full h-full flex items-center justify-center" @click="setExpandAll('expanded')"><ChevronDownMiniSVG /></div>
                        </div>

                    </div>
                    <div v-else class="flex justify-start w-full h-full">
                        <div class="px-2 py-1.5">
                            {{ formatHeader(header.fieldName) }}
                        </div>
                    </div>
                </th>
            </thead>
            <tbody class="overflow-y-auto -top-[1px] relative">
                <CRMTargetListRow
                    v-for="company in reportedCompanies" :key="company.id"
                    :company="company"
                    :headers="columnHeaders"
                    :select-all="selectAll"
                    :expand-all="expandAll"
                    :clearedSelectedResults="clearedSelectedResults"
                    :toggleRowClick="toggleRowClick"
                    :condensed-view="condensedView"
                    :changed-reported-event="changedCompanyReportedEvent(company)"
                    :newCRMActions="newCRMActions"
                    :overrideCRMDetailsFlag="overrideCRMDetailsFlag"
                    :bulkCRMDetailId="bulkCRMDetailId"
                    :selectedResultIds="selectedResultIds"
                    @select-row="addSelectedResult"
                    @deselect-row="removeSelectedResult"
                    @set-reported-event="setReportedEvent"
                    @set-expand-all-neutral="setExpandAll"
                    @open-crm-details="openCRMDetails"
                    @open-company-modal="openCompanyModal"
                    @add-company-to-progress-update="addCompanyToProgressUpdate"
                />  
            </tbody>
        </table>
    </div>
</template>

<script setup>
import { ref, reactive, defineProps, defineEmits, watch, computed, onMounted } from 'vue';
import SelectAll from '../base-components/SelectAll.vue';
import ChevronUpMiniSVG from '@/components/svg-components/ChevronUpMiniSVG.vue';
import ChevronDownMiniSVG from '@/components/svg-components/ChevronDownMiniSVG.vue';
import CRMTargetListRow from './CRMTargetListRow.vue';

const props = defineProps([
    'printView',
    'condensedView',
    'clientName',
    'campaigns',
    'campaignOptions',
    'reportedCompanies',
    'companyIdsToUpdate',
    'bulkCRMDetailId',
    'overrideCRMDetailsFlag'
])

const emit = defineEmits([
    'set-reported-event',
    'refetch',
    'add-company-to-progress-update',
    'open-crm-details',
    'open-company-modal',
    'flag-selected-results',
    'flag-no-selected-results'
])

const columnHeaders = ref([])
const expandAll = ref('neutral')
const toggleRowClick = ref(false)
const orderBy = ref(['computed_crm_status'])

const selectAll = ref(false);
const selectedResultIds = ref([]);
const clearedSelectedResults = ref(false);

const newCRMActions = ref([
    {
        id: -5,
        action: {
            id: 41,
            action_name: 'Other: Dead'
        }
    },
    {
        id: -7,
        action: {
            id: 25,
            action_name: 'Other: Not Now / Keep Warm'
        }
    },
    {
        id: -4,
        action: {
            id: 42,
            action_name: 'Other: Active - In Dialogue'
        }
    },
    {
        id: -6,
        action: {
            id: 31,
            action_name: 'Other: Responded / Coverage'
        }
    },
    {
        id: -3,
        action: {
            id: 43,
            action_name: 'Other: No Response'
        }
    },
    {
        id: -2,
        action: {
            id: 44,
            action_name: 'Other: Not Contacted'
        }
    },
])

watch(() => props.reportedCompanies, () => {
    // console.log('CRMTargetList, props.reportedCompanies changed')
}, {deep: true})

watch(() => props.condensedView, () => {
    if(props.condensedView) {
        showCoreColumns()
    } else {
        showAllColumns()
        
    }
})

const targetListStyle = computed(() => {
    if(props.condensedView) {
        return 'width: 784px; max-width: 784px; min-width: 784px;'
    } else {
        return 'width: 100%';
    }
})

onMounted(() => {
    if(props.reportedCompanies && props.reportedCompanies.length > 0) {
        showAllColumns()
        // showAllColumns()
    }
})

const printedHeaders = computed(() => {
    if(columnHeaders.value && columnHeaders.value.length > 0) {
        return columnHeaders.value.slice(1, 5)
    }
    return []
})

function toggleSelectAll() {
    if(selectAll.value) {
        clearSelectAllClearSelectedResults()
    } else {
        selectAll.value = true;

    }
}

function clearSelectAllClearSelectedResults() {
    clearSelectAll();
    clearSelectedResults();
}

function clearSelectAll() {
  if(selectAll.value) {
    selectAll.value = false;
  }
}

function clearSelectedResults() {
    // console.log('CustomTable: clearSelectedResults()')
    selectedResultIds.value = []
    clearedSelectedResults.value = !clearedSelectedResults.value;
    emit('flag-no-selected-results')
}

function addSelectedResult(resultRowId) {
    let foundIndex = selectedResultIds.value.indexOf(resultRowId)
    if (foundIndex === -1){
        selectedResultIds.value.push(resultRowId);
    }
    emit('flag-selected-results')
    
    // console.log('selected results', selectedResultIds.value)
}

function removeSelectedResult(resultRowId) {
    let foundIndex = selectedResultIds.value.indexOf(resultRowId);
  if (foundIndex !== -1) {
    selectedResultIds.value.splice(foundIndex, 1);
  }
  if(selectedResultIds.value.length == 0) {
    emit('flag-no-selected-results')
  }
}

function showCoreColumns() {
    columnHeaders.value = [
        {fieldName: 'expand_collapse_all'},
        {fieldName: 'computed_crm_status'},
        {fieldName: 'company_name'},
        {fieldName: 'reported_crm_event'},
        {fieldName: ''}
    ]
}

function showAllColumns() {
    const companyObject = props.reportedCompanies[0]
    columnHeaders.value = Object.keys(companyObject).map((companyField, index) => {
        switch(companyField) {
            case 'computed_crm_status':
                return {fieldName: companyField, display: true, ordinal: 0}
            case 'company_name':
                return {fieldName: companyField, display: true, ordinal: index}
            case 'campaign':
                return {fieldName: companyField, display: true, ordinal: 1.75}
            case 'reported_crm_event':
                return {fieldName: companyField, display: true, ordinal: 1.5}
            default:
                return {fieldName: companyField, display: true, ordinal: index}
        }
    }).sort((a, b) => {return a.ordinal - b.ordinal})
}

function setExpandAll(expandAllValue) {
    // console.log('setExpandAll', expandAllValue)
    expandAll.value = expandAllValue
}

function refetch() {
    emit('refetch')
}


function openCRMDetails(company) {
    emit('open-crm-details', company)
}

function openCompanyModal(company) {
    emit('open-company-modal', company)
}

function addCompanyToProgressUpdate(companyID) {
    // console.log('CRMTargetList: addCompanyToProgressUpdate')
    emit('add-company-to-progress-update', companyID)
}


// 3 cols
function getHeaderStyleByIndex(index) {
    if(props.condensedView) {
        if(index == 0) {
            return `position: sticky; max-width: 52px; width: 52px; min-width: 52px; left: 0px; z-index: 20;`
        } else if(index == 1) {
            return `position: sticky; max-width: 200px; width: 200px; min-width: 200px; left: 52px; z-index: 20;` // 300
        } else if(index == 2) {
            return `position: sticky; max-width: 300px; width: 300px; min-width: 300px; left: 252px; z-index: 20;` // 150
        } else if (index == 3) {
            return `position: sticky; max-width: 200px; width: 200px; min-width: 200px; left: 552px; z-index: 20; border-right: 2px solid #D1D5DB;`
        } else if (index == 4) {
            return `position: sticky; max-width: 30px; width: 30px; min-width: 30px; left: 752px; z-index: 20;`
        } else {
            return 'z-index: 10'
        }
    } else {
        if(index == 0) {
            return `position: sticky; max-width: 52px; width: 52px; min-width: 52px; left: 0px; z-index: 20;`
        } else if(index == 1) {
            return `position: sticky; max-width: 200px; width: 200px; min-width: 200px; left: 52px; z-index: 20;` // 300
        } else if(index == 2) {
            return `position: sticky; max-width: 300px; width: 300px; min-width: 300px; left: 252px; z-index: 20;` // 150
        } else if (index == 3) {
            return `position: sticky; max-width: 200px; width: 200px; min-width: 200px; left: 552px; z-index: 20; border-right: 2px solid #D1D5DB;`
        } else {
            return 'z-index: 10'
        }
    }

}

function formatHeader(fieldName) {
    if(fieldName == 'reported_crm_event') {
        if(props.printView) {
            return 'CRM Detail'
        } else {
            return 'Reported Detail (*Auto)'
        }
    } else if(fieldName == 'computed_crm_status') {
        return 'CRM Status'
    }
    let headerLabel = fieldName.replaceAll('_', ' ').replace(/(^| )(\w)/g, function(x) {return x.toUpperCase();}).replaceAll('Url', 'URL').replaceAll('Crm', 'CRM')
    return headerLabel
}

function changedCompanyReportedEvent(company) {
    if(props.companyIdsToUpdate.includes(company.id)) {
        return true
    }
    return false
}

function setReportedEvent(company, event, autoCompute) {
    emit('set-reported-event', company, event, autoCompute)
}

</script>